// @ts-strict-ignore
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromDvrSettings from '../reducers/dvr-settings.reducer';
import { DvrSettings } from '../model/dvr-settings';
import { filterVehiclesByGroup } from '../../settings/dvr-settings/dvr-settings-table/dvr-settings-table.component';
import {
  SelectionVehicle,
  TableDvrSettings,
} from '../../settings/models/dvr-settings.model';
import { Vehicle, VehicleGroup } from '@fc-core';
import { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';

export const getDvrSettingsState = createFeatureSelector<fromDvrSettings.State>(
  fromDvrSettings.key,
);
export const getDvrSettings = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getDvrSettings,
);

export const getDvrSettingsLoading = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getLoading,
);

export const getUpdatingCameraIds = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getUpdatingCameraIds,
);

export const getDvrSettingsFilters = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getDvrSettingsFilters,
);

export const getSelectedDvrFilters = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getSelectedFilters,
);

export const getDvrVehicleGroupsFilter = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getDvrVehicleGroupsFilter,
);
export const getDvrSelectedVehicle = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getSelectedVehicle,
);

export const getDvrSelectedVehicles = createSelector(
  getDvrSettingsState,
  fromDvrSettings.getSelectedVehicles,
);

export const getDvrSettingsById = (cameraId: number) =>
  createSelector(getDvrSettings, (settings: DvrSettings[]) =>
    settings.find((setting) => setting.camera.id === cameraId),
  );

export const getDvrTableData = (vehicleList: Vehicle[]) =>
  createSelector(
    getDvrSelectedVehicles,
    getDvrSelectedVehicle,
    getDvrVehicleGroupsFilter,
    getSelectedDvrFilters,
    getDvrSettings,
    (
      selectedVehicles: SelectionVehicle[],
      selectedVehicle: Vehicle,
      vehicleGroupFilter: VehicleGroup[],
      selectedDvrFilters: FilterItem[],
      dvrSettings: DvrSettings[],
    ) => {
      if (!dvrSettings?.length || !vehicleList?.length) return;

      const vehiclesFilteredBySettings = vehicleList
        .filter((vehicle: Vehicle) =>
          dvrSettings.some((setting) => setting.vehicle?.id === vehicle?.id),
        )
        .map((vehicle: Vehicle) =>
          resolveVehicleFromSettings(vehicle, selectedVehicles),
        );

      return vehiclesFilteredBySettings
        .map((vehicle: SelectionVehicle) =>
          buildTableDataItem(vehicle, dvrSettings),
        )
        .filter((tableData: TableDvrSettings) =>
          filterVehicles(tableData, selectedDvrFilters),
        )
        .filter((tableData: TableDvrSettings) =>
          filterVehiclesByGroup(tableData?.vehicle, vehicleGroupFilter),
        )
        .filter((setting: TableDvrSettings) =>
          selectedVehicle ? setting.vehicle.id === selectedVehicle.id : true,
        );
    },
  );

const resolveVehicleFromSettings = (
  vehicle: Vehicle,
  selectedVehicles: SelectionVehicle[],
): SelectionVehicle => {
  const ship = selectedVehicles.find(
    (selVehicle) => selVehicle.id === vehicle.id,
  );
  return (ship ? ship : vehicle) as SelectionVehicle;
};

const buildTableDataItem = (
  vehicle: SelectionVehicle,
  dvrSettings: DvrSettings[],
): TableDvrSettings => {
  const currentVehicleCameraSettings: DvrSettings = dvrSettings.find(
    (dvrSetting) => dvrSetting?.vehicle?.id === vehicle.id,
  );
  return {
    vehicle: { ...vehicle, enabled: vehicle.enabled },
    orientation: currentVehicleCameraSettings?.orientation,
    bump_sensitivity: currentVehicleCameraSettings?.bumpSensitivity,
    inside_camera: currentVehicleCameraSettings?.insideCameraEnabled,
    resolution: currentVehicleCameraSettings?.cameraResolution,
    insideCameraAccess: currentVehicleCameraSettings.insideCameraAccess,
    isPending: currentVehicleCameraSettings.isPending,
  };
};

const filterVehicles = (
  tableData: TableDvrSettings,
  selectedFilters: FilterItem[],
): boolean => {
  if (!selectedFilters?.length) return true;
  const selectedFiltersIds = selectedFilters.map((filter) => filter.id);
  const settingsValues = Object.entries(tableData)
    .splice(1)
    .map(([key, value]) => {
      if (key === 'insideCameraAccess')
        return tableData[key] ? 'ica_on' : 'ica_off';
      if (key === 'inside_camera')
        return tableData[key] ? 'inside_camera_on' : 'inside_camera_off';
      return value;
    });
  return selectedFiltersIds.some((id: string) =>
    settingsValues
      .map((i) => i.toString().toLowerCase())
      .includes(id.toLowerCase()),
  );
};
